import { useEffect, useState } from 'react';

type Family = 'precast' | 'curbthroat' | 'castiron' | 'nyloplast';

interface Tile {
  src: string;
  label: string;
  height?: number;
}

interface FamilyContent {
  title: string;
  baseLocation: string;
  renderings: Tile[];
  installations: Tile[];
  internal: Tile[];
}

const CONTENT: Record<Family, FamilyContent> = {
  precast: {
    title: 'Precast Concrete Structures with\u00a0Grates',
    baseLocation: '/RESOURCES/PrecastConcrete',
    renderings: [
      { src: 'Precast-RoundPrecast.jpg', label: 'Round Precast', height: 210 },
      { src: 'Precast-SquareGrate4SeatSupport.jpg', label: 'Square Grate 4 Seat\u00a0Support', height: 210 },
      { src: 'Precast-RectangularGrate4Seat.jpg', label: 'Rectangular Grate 4\u00a0Seat', height: 210 },
      { src: 'Precast-RectangularGrate2SeatSupport.jpg', label: 'Rectangular Grate 2 Seat Support (Long\u00a0Span)', height: 210 },
      { src: 'Precast-CatchBasin.jpg', label: 'Precast Catch\u00a0Basin', height: 210 },
    ],
    installations: [
      { src: 'Precast-HDRectangularinstalled.jpg', label: 'Rectangular HD\u00a0Filter', height: 140 },
      { src: 'Precast-HDRoundinstalledtop.jpg', label: 'Round HD Top\u00a0View', height: 140 },
      { src: 'Precast-HDRoundinstalledunderside.jpg', label: 'Round HD Underside\u00a0View', height: 140 },
    ],
    internal: [
      { src: 'ADS-FlexStorm-LITE.jpg', label: 'Lite Inlet Filters' },
      { src: 'ADS-FlexStorm-PURE.jpg', label: 'Pure Inlet Filters' },
    ],
  },
  castiron: {
    title: 'Cast Iron Frame and\u00a0Grate',
    baseLocation: '/RESOURCES/CastIron',
    renderings: [
      { src: 'CastIron-Round.png', label: 'Round Frame and\u00a0Grate', height: 154 },
      { src: 'CastIron-FlatRectangular.png', label: 'Flat-Rectangular Frame and\u00a0Grate', height: 154 },
      { src: 'CastIron-CurbBox.png', label: 'Curb Box\u00a0Combination', height: 154 },
    ],
    installations: [
      { src: 'CastIron-rectangularframe.jpg', label: 'Rectangular Frame and Grate\u00a0Installed', height: 150 },
      { src: 'CastIron-rectangulartype24.jpg', label: 'Rectangular Frame Installed with\u00a0Grate', height: 150 },
      { src: 'CastIron-Round.jpg', label: 'Round Frame\u00a0Installed', height: 150 },
      { src: 'CastIron-Roundgrate.jpg', label: 'Round Frame Installed with\u00a0Grate', height: 150 },
      { src: 'CastIron-RolledCurb.jpg', label: 'Rolled Curb Frame and Grate\u00a0Installed', height: 150 },
      { src: 'CastIron-RolledCurbgrate.jpg', label: 'Rolled Curb Frame Installed with\u00a0Grate', height: 150 },
      { src: 'CastIron-V-grate.jpg', label: 'V-Grate Frame\u00a0Installed', height: 150 },
      { src: 'CastIron-V-grategrate.jpg', label: 'V-Grate Frame Installed with\u00a0Grate', height: 150 },
      { src: 'CastIron-CurbBoxgrate.jpg', label: 'Curb Box Installed with\u00a0Grate', height: 150 },
    ],
    internal: [
      { src: 'ADS-FlexStorm-LITE.jpg', label: 'Lite Inlet Filters' },
      { src: 'ADS-FlexStorm-PURE.jpg', label: 'Pure Inlet Filters' },
    ],
  },
  curbthroat: {
    title: 'Open Throat Curb\u00a0Inlets',
    baseLocation: '/RESOURCES/OpenThroat',
    renderings: [
      { src: 'OpenThroat-curbinlet.jpg', label: 'Open Throat Curb Inlet', height: 100 },
      { src: 'OpenThroat-WallMountRendering.jpg', label: 'Open Throat Curb Inlet', height: 100 },
      { src: 'openthroatexample.jpg', label: 'Open Throat Curb Inlet', height: 100 },
      { src: 'OpenThroat-wallmounttop.jpg', label: 'Open Throat Curb Inlet', height: 100 },
    ],
    installations: [
      { src: 'OpenThroat-WallMountRendering.jpg', label: 'Wall Mounted', height: 100 },
    ],
    internal: [
      { src: 'ADS-FlexStorm-LITE.jpg', label: 'Lite Inlet Filters' },
      { src: 'ADS-FlexStorm-PURE.jpg', label: 'Pure Inlet Filters' },
    ],
  },
  nyloplast: {
    title: 'Nyloplast\u00a0Castings',
    baseLocation: '/RESOURCES/Nyloplast',
    renderings: [
      { src: 'NyloplastFamily.jpg', label: 'Nyloplast Castings', height: 120 },
      { src: 'NyloTopIso.jpg', label: 'Nyloplast Castings', height: 120 },
      { src: 'renderNyloRound.jpg', label: 'Nyloplast Castings', height: 120 },
      { src: 'renderNyloRect.jpg', label: 'Nyloplast Castings', height: 120 },
    ],
    installations: [
      { src: 'NyloplastSingle.jpg', label: 'FlexStorm Filter for a Nyloplast\u00a0Casting', height: 220 },
      { src: 'Nyloplast-cutaway.jpg', label: 'Nyloplast Casting', height: 220 },
      { src: 'NyloTopIso.jpg', label: 'Nyloplast Casting', height: 220 },
    ],
    internal: [
      { src: 'ADS-FlexStorm-LITE.jpg', label: 'Lite Inlet Filters' },
      { src: 'ADS-FlexStorm-PURE.jpg', label: 'Pure Inlet Filters' },
    ],
  },
};

function Section({
  iconSrc,
  title,
  tooltip,
  baseLocation,
  tiles,
  onOpen,
}: {
  iconSrc: string;
  title: string;
  tooltip: string;
  baseLocation: string;
  tiles: Tile[];
  onOpen: (src: string) => void;
}) {
  if (tiles.length === 0) return null;
  return (
    <>
      <h2 className="d-flex align-items-center mt-4">
        <img className="img-fluid pe-2" src={iconSrc} alt="" style={{ height: 28, width: 'auto' }} />
        <span className="pt-1">{title}</span>
        <img
          className="img-fluid ps-2"
          src="/images/ICON/Icon_Question_Filled_Green.png"
          alt=""
          title={tooltip}
          style={{ height: 24, width: 'auto', cursor: 'help' }}
        />
      </h2>
      <div className="text-primary fs-5 text-start">Click to Expand</div>
      <div className="row text-center align-items-start">
        {tiles.map((tile, i) => (
          <div key={`${tile.src}-${i}`} className="col-12 col-md-6 col-lg-3 mb-3">
            <div
              className="align-items-center"
              style={tile.height ? { height: tile.height } : undefined}
            >
              <a
                style={{ cursor: 'zoom-in' }}
                onClick={() => onOpen(`${baseLocation}/${tile.src}`)}
              >
                <img
                  className="img-fluid"
                  style={tile.height ? { maxHeight: tile.height, height: 'auto', width: 'auto' } : { height: 'auto', width: 'auto' }}
                  src={`${baseLocation}/${tile.src}`}
                  alt={tile.label}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </a>
            </div>
            <a style={{ cursor: 'zoom-in' }} onClick={() => onOpen(`${baseLocation}/${tile.src}`)}>
              <p className="fs-6 pt-2">{tile.label}</p>
            </a>
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * "More Information" overlay for each structure family. Mirrors the legacy
 * `resources/jsx/components/docs/*docs.jsx` modal: three sections (Structural
 * Renderings, Installation Photos, Internal Assets) of clickable thumbnails.
 * Click a thumbnail to open a full-screen lightbox.
 */
export function FamilyInfoModal({
  family,
  isAdsEmployee,
  onClose,
}: {
  family: Family;
  isAdsEmployee: boolean;
  onClose: () => void;
}) {
  const content = CONTENT[family];
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxSrc) setLightboxSrc(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, lightboxSrc]);

  return (
    <>
      <div
        className="modal fade show"
        style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1055,
          overflow: 'auto',
        }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="modal-dialog modal-dialog-scrollable modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn-close p-2"
                aria-label="Close"
                onClick={onClose}
              />
            </div>
            <div className="modal-body ps-2 ps-lg-5">
              <h1>{content.title}</h1>

              <Section
                iconSrc="/images/ICON/Icon_Transform_Small.png"
                title="Structural Renderings"
                tooltip="3D renderings of this structure type"
                baseLocation={content.baseLocation}
                tiles={content.renderings}
                onOpen={setLightboxSrc}
              />

              <Section
                iconSrc="/images/ICON/Icon_Image_Small.png"
                title="Installation Photos"
                tooltip="Photos of these structures installed on site"
                baseLocation={content.baseLocation}
                tiles={content.installations}
                onOpen={setLightboxSrc}
              />

              {isAdsEmployee && (
                <Section
                  iconSrc="/images/ICON/Icon_Hide_Green.png"
                  title="Internal Assets"
                  tooltip="Assets for ADS employees only. Please do not share publicly"
                  baseLocation={content.baseLocation}
                  tiles={content.internal}
                  onOpen={setLightboxSrc}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.92)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out',
            padding: 24,
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
        >
          <img
            src={lightboxSrc}
            alt=""
            style={{ maxWidth: '95vw', maxHeight: '95vh', height: 'auto', width: 'auto', objectFit: 'contain' }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="btn-close btn-close-white"
            aria-label="Close"
            onClick={() => setLightboxSrc(null)}
            style={{ position: 'absolute', top: 16, right: 16 }}
          />
        </div>
      )}
    </>
  );
}
